import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CASE_STUDIES, GRID_ENTRIES, BENEFITS } from "@/data/portfolio";
import Benefit from "@/components/site/Benefit";
import { track } from "@/lib/analytics";
import { T, useLang } from "@/lib/i18n";


export default function Projects() {
  const { t } = useLang();
  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="border-t border-[#e5e0d8] section-pad bg-[#f2ece4]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="font-mono-label text-[11px] text-[#9a7218]">
              {t("04 · My top 3 case studies")}
            </div>
          </div>
          <div className="font-mono-label text-[10px] text-[#86928a]">
            {t("Discovery → Product → Executive dashboard")}
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {CASE_STUDIES.filter((c) => !c.secondary).map((cs, i) => (
            <Link
              key={cs.slug}
              to={`/case-studies/${cs.slug}`}
              data-testid={`case-study-card-${cs.slug}`}
              className="group flex flex-col bg-white border border-[#e5e0d8] hover:border-[#14352a] tilt-hover"
              style={{ transitionDelay: `${i * 40}ms` }}
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#f2ece4] border-b border-[#e5e0d8] relative">
                <img
                  src={cs.hero}
                  alt={cs.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  loading="lazy"
                />
                <span className="absolute top-3 left-3 px-3 py-1 rounded-md bg-[#14352a] text-white text-xs font-medium">
                  {t("Case study")}
                </span>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <div data-testid={`case-study-period-${cs.slug}`} className="text-xs text-[#55625b]">{cs.story?.period}</div>
                <h3 className="mt-2 font-display text-xl text-[#14352a] leading-tight">
                  {t(cs.title)}
                </h3>
                <p className="mt-2 text-sm text-[#55625b] leading-relaxed">
                  {t(cs.subtitle)}
                </p>
                {cs.story?.tags && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {cs.story.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-md bg-[#f2ece4] text-xs font-medium text-[#14352a]">{t(tag)}</span>
                    ))}
                  </div>
                )}
                <div className="mt-auto pt-4 flex items-center gap-2 text-sm text-[#9a7218] font-medium">
                  {t("Read case study")}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          {GRID_ENTRIES.map((g, i) => (
            <article
              key={i}
              data-testid={`grid-entry-${i}`}
              className="p-4 bg-white/70 border border-[#e5e0d8]"
            >
              <div className="font-mono-label text-[9px] text-[#9a7218]">
                {t(g.tag)}
              </div>
              <h4 className="mt-1.5 font-display text-base text-[#14352a] leading-snug">
                {t(g.title)}
              </h4>
              <p className="mt-1.5 text-xs text-[#55625b] leading-relaxed">
                {t(g.body)}
              </p>
              {g.to && (
                <Link
                  to={g.to}
                  data-testid={`grid-entry-link-${i}`}
                  onClick={() => track("case_open", { slug: g.to.split("/").pop(), from: "grid" })}
                  className="mt-3 inline-flex items-center gap-1 font-mono-label text-[9px] text-[#9a7218] link-underline"
                >
                  {t(g.cta)} <ArrowUpRight className="h-3 w-3" />
                </Link>
              )}
              {g.quote && (
                <figure data-testid={`grid-entry-quote-${i}`} className="mt-3 pt-3 border-t border-[#e5e0d8]">
                  <blockquote className="font-display italic text-[13px] text-[#14352a] leading-snug">&ldquo;{g.quote.text}&rdquo;</blockquote>
                  <figcaption className="mt-1 font-mono-label text-[8px] text-[#86928a]">{g.quote.who}</figcaption>
                </figure>
              )}
            </article>
          ))}
        </div>

        <Benefit text={BENEFITS.projects} testid="benefit-projects" />
      </div>
    </section>
  );
}
